import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { api } from "@/lib/api";
import { PageHeader, Loading, StatCard, money, moneyShort } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Wallet, AlertTriangle, CheckCircle2, LifeBuoy } from "lucide-react";

const COLORS = ["#0066FF", "#00C4FF", "#8B5CF6", "#10B981", "#F59E0B", "#F97316", "#EF4444"];
const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 };

export default function Analytics() {
  const { data, isLoading } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/analytics")).data });
  if (isLoading || !data) return <div><PageHeader title="Analytics" /><Loading rows={6} /></div>;

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Sales, finance, marketing and support insights" testid="analytics-header" />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={CheckCircle2} label="Collected" value={moneyShort(data.finance.collected)} tone="success" index={0} />
        <StatCard icon={Wallet} label="Outstanding" value={moneyShort(data.finance.outstanding)} tone="warning" index={1} />
        <StatCard icon={AlertTriangle} label="Overdue" value={moneyShort(data.finance.overdue)} tone="critical" index={2} />
        <StatCard icon={LifeBuoy} label="Tickets resolved" value={`${data.support.resolved}/${data.support.total}`} tone="info" index={3} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Salesperson performance (won value)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.salesperson} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={moneyShort} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#0066FF" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Leads by source (ROI)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.source_roi}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="source" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-12} textAnchor="end" height={50} interval={0} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="leads" radius={[6, 6, 0, 0]}>
                {data.source_roi.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Invoices by status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.finance.by_status}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="status" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-12} textAnchor="end" height={50} interval={0} tickFormatter={(s) => s.replace("_", " ")} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Tickets by priority</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.support.by_priority}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="priority" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.support.by_priority.map((_, i) => <Cell key={i} fill={["#94a3b8", "#3B82F6", "#F97316", "#EF4444"][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
